import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Star, Moon, Sun, Globe, Compass, MapPin, RefreshCw, 
  Maximize2, Minimize2, Search, Info, X, Satellite,
  ChevronLeft, ChevronRight, ZoomIn, ZoomOut
} from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Constellation data with star positions (simplified for visualization)
const CONSTELLATIONS = {
  'Orion': {
    stars: [
      { name: 'Betelgeuse', x: 0.2, y: 0.15, magnitude: 0.5 },
      { name: 'Rigel', x: 0.8, y: 0.85, magnitude: 0.1 },
      { name: 'Bellatrix', x: 0.75, y: 0.15, magnitude: 1.6 },
      { name: 'Saiph', x: 0.25, y: 0.85, magnitude: 2.1 },
      { name: 'Alnitak', x: 0.35, y: 0.5, magnitude: 1.7 },
      { name: 'Alnilam', x: 0.5, y: 0.5, magnitude: 1.7 },
      { name: 'Mintaka', x: 0.65, y: 0.5, magnitude: 2.2 },
    ],
    lines: [[0,2], [2,6], [6,5], [5,4], [4,0], [0,3], [3,1], [1,2]],
    description: 'The Hunter - One of the most recognizable constellations'
  },
  'Ursa Major': {
    stars: [
      { name: 'Dubhe', x: 0.15, y: 0.2, magnitude: 1.8 },
      { name: 'Merak', x: 0.25, y: 0.35, magnitude: 2.4 },
      { name: 'Phecda', x: 0.45, y: 0.45, magnitude: 2.4 },
      { name: 'Megrez', x: 0.55, y: 0.35, magnitude: 3.3 },
      { name: 'Alioth', x: 0.7, y: 0.4, magnitude: 1.8 },
      { name: 'Mizar', x: 0.8, y: 0.5, magnitude: 2.1 },
      { name: 'Alkaid', x: 0.9, y: 0.65, magnitude: 1.9 },
    ],
    lines: [[0,1], [1,2], [2,3], [3,4], [4,5], [5,6], [3,0]],
    description: 'The Great Bear - Contains the Big Dipper'
  },
  'Cassiopeia': {
    stars: [
      { name: 'Schedar', x: 0.1, y: 0.5, magnitude: 2.2 },
      { name: 'Caph', x: 0.25, y: 0.3, magnitude: 2.3 },
      { name: 'Gamma Cas', x: 0.5, y: 0.55, magnitude: 2.2 },
      { name: 'Ruchbah', x: 0.7, y: 0.35, magnitude: 2.7 },
      { name: 'Segin', x: 0.9, y: 0.5, magnitude: 3.4 },
    ],
    lines: [[0,1], [1,2], [2,3], [3,4]],
    description: 'The Queen - W-shaped constellation'
  },
  'Leo': {
    stars: [
      { name: 'Regulus', x: 0.3, y: 0.7, magnitude: 1.4 },
      { name: 'Denebola', x: 0.85, y: 0.5, magnitude: 2.1 },
      { name: 'Algieba', x: 0.35, y: 0.35, magnitude: 2.0 },
      { name: 'Zosma', x: 0.7, y: 0.45, magnitude: 2.6 },
      { name: 'Chertan', x: 0.6, y: 0.6, magnitude: 3.3 },
      { name: 'Adhafera', x: 0.4, y: 0.25, magnitude: 3.4 },
    ],
    lines: [[0,2], [2,5], [5,3], [3,1], [3,4], [4,0]],
    description: 'The Lion - Zodiac constellation'
  },
  'Scorpius': {
    stars: [
      { name: 'Antares', x: 0.4, y: 0.35, magnitude: 1.0 },
      { name: 'Shaula', x: 0.85, y: 0.85, magnitude: 1.6 },
      { name: 'Sargas', x: 0.75, y: 0.75, magnitude: 1.9 },
      { name: 'Dschubba', x: 0.3, y: 0.2, magnitude: 2.3 },
      { name: 'Acrab', x: 0.25, y: 0.15, magnitude: 2.6 },
    ],
    lines: [[4,3], [3,0], [0,2], [2,1]],
    description: 'The Scorpion - Zodiac constellation with red Antares'
  },
  'Cygnus': {
    stars: [
      { name: 'Deneb', x: 0.5, y: 0.1, magnitude: 1.3 },
      { name: 'Sadr', x: 0.5, y: 0.4, magnitude: 2.2 },
      { name: 'Gienah', x: 0.25, y: 0.55, magnitude: 2.5 },
      { name: 'Delta Cyg', x: 0.75, y: 0.55, magnitude: 2.9 },
      { name: 'Albireo', x: 0.5, y: 0.9, magnitude: 3.1 },
    ],
    lines: [[0,1], [1,4], [2,1], [1,3]],
    description: 'The Swan - Northern Cross pattern'
  },
  'Gemini': {
    stars: [
      { name: 'Castor', x: 0.35, y: 0.15, magnitude: 1.6 },
      { name: 'Pollux', x: 0.45, y: 0.25, magnitude: 1.2 },
      { name: 'Alhena', x: 0.7, y: 0.8, magnitude: 1.9 },
      { name: 'Wasat', x: 0.55, y: 0.5, magnitude: 3.5 },
      { name: 'Mebsuta', x: 0.3, y: 0.45, magnitude: 3.1 },
    ],
    lines: [[0,4], [4,3], [3,2], [1,3]],
    description: 'The Twins - Zodiac constellation'
  },
  'Taurus': {
    stars: [
      { name: 'Aldebaran', x: 0.4, y: 0.45, magnitude: 0.9 },
      { name: 'Elnath', x: 0.85, y: 0.2, magnitude: 1.7 },
      { name: 'Alcyone', x: 0.2, y: 0.3, magnitude: 2.9 },
      { name: 'Ain', x: 0.45, y: 0.4, magnitude: 3.5 },
    ],
    lines: [[2,0], [0,3], [3,1]],
    description: 'The Bull - Contains the Pleiades star cluster'
  }
};

// Planet visual data
const PLANETS = {
  'Mercury': { color: '#b5b5b5', size: 8, ring: false },
  'Venus': { color: '#ffd27f', size: 12, ring: false },
  'Mars': { color: '#ff6b4a', size: 10, ring: false },
  'Jupiter': { color: '#e8c59e', size: 22, ring: false },
  'Saturn': { color: '#f4d59e', size: 18, ring: true },
  'Uranus': { color: '#c9f4ff', size: 14, ring: true },
  'Neptune': { color: '#5b8fff', size: 14, ring: false },
};

const StarMap = ({ isOpen, onClose }) => {
  const { theme } = useTheme();
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [celestialData, setCelestialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedObject, setSelectedObject] = useState(null);
  const [userLocation, setUserLocation] = useState({ lat: 40.7128, lon: -74.0060 }); // Default NYC
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [viewRotation, setViewRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [showConstellationLines, setShowConstellationLines] = useState(true);
  const [showLabels, setShowLabels] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedConstellation, setSelectedConstellation] = useState(null);

  // Fetch celestial data
  const fetchCelestialData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/celestial/current`, {
        params: { lat: userLocation.lat, lon: userLocation.lon }
      });
      setCelestialData(response.data);
    } catch (error) {
      console.error('Failed to fetch celestial data:', error);
      toast.error('Failed to load celestial data');
    } finally {
      setLoading(false);
    }
  }, [userLocation]);

  // Get user location
  const getUserLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lon: position.coords.longitude
          });
          toast.success('Location updated');
        },
        (error) => {
          console.error('Geolocation error:', error);
          toast.error('Could not get location');
        }
      );
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    if (isOpen) {
      fetchCelestialData();
      getUserLocation();
    }
  }, [isOpen, fetchCelestialData, getUserLocation]);

  // Update time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Draw star map on canvas
  useEffect(() => {
    if (!canvasRef.current || !isOpen) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    const centerX = width / 2;
    const centerY = height / 2;

    // Clear canvas
    ctx.fillStyle = '#02040a';
    ctx.fillRect(0, 0, width, height);

    // Draw circular star map boundary
    ctx.save();
    ctx.beginPath();
    ctx.arc(centerX, centerY, Math.min(centerX, centerY) - 20, 0, Math.PI * 2);
    ctx.clip();

    // Draw gradient background
    const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, Math.min(centerX, centerY));
    gradient.addColorStop(0, '#0a1020');
    gradient.addColorStop(1, '#02040a');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // Draw random background stars
    for (let i = 0; i < 200; i++) {
      const angle = Math.random() * Math.PI * 2 + viewRotation;
      const dist = Math.random() * Math.min(centerX, centerY) * 0.95;
      const x = centerX + Math.cos(angle) * dist * zoom;
      const y = centerY + Math.sin(angle) * dist * zoom;
      const size = Math.random() * 1.5 + 0.5;
      const alpha = Math.random() * 0.5 + 0.3;
      
      ctx.beginPath();
      ctx.arc(x, y, size, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
      ctx.fill();
    }

    // Draw constellations
    if (celestialData?.visible_constellations) {
      celestialData.visible_constellations.forEach((name, index) => {
        const constellation = CONSTELLATIONS[name];
        if (!constellation) return;

        const baseAngle = (index / celestialData.visible_constellations.length) * Math.PI * 2 + viewRotation;
        const baseDist = 0.3 + (index % 3) * 0.2;
        const baseX = centerX + Math.cos(baseAngle) * centerX * baseDist * zoom;
        const baseY = centerY + Math.sin(baseAngle) * centerY * baseDist * zoom;
        const scale = 80 * zoom;

        // Draw constellation lines
        if (showConstellationLines) {
          ctx.strokeStyle = selectedConstellation === name 
            ? theme.colors.primary 
            : 'rgba(100, 150, 255, 0.3)';
          ctx.lineWidth = selectedConstellation === name ? 2 : 1;
          
          constellation.lines.forEach(([from, to]) => {
            const star1 = constellation.stars[from];
            const star2 = constellation.stars[to];
            ctx.beginPath();
            ctx.moveTo(baseX + (star1.x - 0.5) * scale, baseY + (star1.y - 0.5) * scale);
            ctx.lineTo(baseX + (star2.x - 0.5) * scale, baseY + (star2.y - 0.5) * scale);
            ctx.stroke();
          });
        }

        // Draw stars
        constellation.stars.forEach((star) => {
          const x = baseX + (star.x - 0.5) * scale;
          const y = baseY + (star.y - 0.5) * scale;
          const size = Math.max(2, 4 - star.magnitude) * zoom;

          // Star glow
          const starGradient = ctx.createRadialGradient(x, y, 0, x, y, size * 3);
          starGradient.addColorStop(0, 'rgba(255, 255, 255, 0.8)');
          starGradient.addColorStop(0.5, 'rgba(200, 220, 255, 0.3)');
          starGradient.addColorStop(1, 'transparent');
          ctx.fillStyle = starGradient;
          ctx.beginPath();
          ctx.arc(x, y, size * 3, 0, Math.PI * 2);
          ctx.fill();

          // Star core
          ctx.beginPath();
          ctx.arc(x, y, size, 0, Math.PI * 2);
          ctx.fillStyle = '#ffffff';
          ctx.fill();
        });

        // Draw constellation label
        if (showLabels) {
          ctx.font = `${12 * zoom}px monospace`;
          ctx.fillStyle = selectedConstellation === name 
            ? theme.colors.primary 
            : 'rgba(150, 180, 255, 0.8)';
          ctx.textAlign = 'center';
          ctx.fillText(name, baseX, baseY - scale * 0.6);
        }
      });
    }

    // Draw celestial objects (planets, moon, sun)
    if (celestialData?.celestial_objects) {
      celestialData.celestial_objects.forEach((obj, index) => {
        const angle = (index / celestialData.celestial_objects.length) * Math.PI * 2 + viewRotation + Math.PI / 4;
        const dist = 0.7 + (index % 2) * 0.15;
        const x = centerX + Math.cos(angle) * centerX * dist * zoom;
        const y = centerY + Math.sin(angle) * centerY * dist * zoom;

        const planet = PLANETS[obj.name];
        const size = planet?.size || 15;
        const color = planet?.color || '#ffd700';

        // Object glow
        const glowGradient = ctx.createRadialGradient(x, y, 0, x, y, size * 2);
        glowGradient.addColorStop(0, color + 'aa');
        glowGradient.addColorStop(0.5, color + '44');
        glowGradient.addColorStop(1, 'transparent');
        ctx.fillStyle = glowGradient;
        ctx.beginPath();
        ctx.arc(x, y, size * 2, 0, Math.PI * 2);
        ctx.fill();

        // Object body
        ctx.beginPath();
        ctx.arc(x, y, size * zoom, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();

        // Saturn's rings
        if (planet?.ring) {
          ctx.beginPath();
          ctx.ellipse(x, y, size * 1.8 * zoom, size * 0.5 * zoom, 0.3, 0, Math.PI * 2);
          ctx.strokeStyle = color + '88';
          ctx.lineWidth = 2;
          ctx.stroke();
        }

        // Moon phase indicator
        if (obj.name === 'Moon' && obj.phase) {
          ctx.font = '10px sans-serif';
          ctx.fillStyle = '#ffffff';
          ctx.textAlign = 'center';
          ctx.fillText(obj.phase, x, y + size * zoom + 15);
        }

        // Label
        if (showLabels) {
          ctx.font = `${11 * zoom}px monospace`;
          ctx.fillStyle = color;
          ctx.textAlign = 'center';
          ctx.fillText(obj.name, x, y - size * zoom - 8);
        }
      });
    }

    // Draw compass directions
    const compassRadius = Math.min(centerX, centerY) - 10;
    ['N', 'E', 'S', 'W'].forEach((dir, i) => {
      const angle = (i * Math.PI / 2) - Math.PI / 2 + viewRotation;
      const x = centerX + Math.cos(angle) * compassRadius;
      const y = centerY + Math.sin(angle) * compassRadius;
      
      ctx.font = 'bold 14px monospace';
      ctx.fillStyle = dir === 'N' ? '#ff4444' : theme.colors.primary;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(dir, x, y);
    });

    ctx.restore();

    // Draw border
    ctx.beginPath();
    ctx.arc(centerX, centerY, Math.min(centerX, centerY) - 20, 0, Math.PI * 2);
    ctx.strokeStyle = theme.colors.primary + '40';
    ctx.lineWidth = 2;
    ctx.stroke();

  }, [celestialData, isOpen, viewRotation, zoom, showConstellationLines, showLabels, selectedConstellation, theme]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={`fixed inset-0 z-50 flex items-center justify-center ${isFullscreen ? '' : 'p-4'}`}
        style={{ background: 'rgba(0,0,0,0.9)' }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className={`relative ${isFullscreen ? 'w-full h-full' : 'w-full max-w-4xl max-h-[90vh]'} rounded-2xl overflow-hidden`}
          style={{
            background: 'linear-gradient(180deg, #0a1020 0%, #02040a 100%)',
            border: isFullscreen ? 'none' : `1px solid ${theme.colors.primary}30`
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div 
            className="flex items-center justify-between p-4 border-b"
            style={{ borderColor: `${theme.colors.primary}20` }}
          >
            <div className="flex items-center gap-3">
              <div 
                className="p-2 rounded-xl"
                style={{ background: `${theme.colors.primary}20` }}
              >
                <Globe className="w-5 h-5" style={{ color: theme.colors.primary }} />
              </div>
              <div>
                <h2 className="font-unbounded font-bold text-lg text-white">Live Star Map</h2>
                <p className="text-xs text-slate-500 font-mono">
                  {currentTime.toLocaleTimeString()} • {userLocation.lat.toFixed(2)}°, {userLocation.lon.toFixed(2)}°
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={fetchCelestialData}
                className="p-2 rounded-xl transition-all hover:bg-white/10"
                disabled={loading}
              >
                <RefreshCw className={`w-5 h-5 text-slate-400 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={getUserLocation}
                className="p-2 rounded-xl transition-all hover:bg-white/10"
              >
                <MapPin className="w-5 h-5 text-slate-400" />
              </button>
              <button
                onClick={() => setIsFullscreen(!isFullscreen)}
                className="p-2 rounded-xl transition-all hover:bg-white/10"
              >
                {isFullscreen ? (
                  <Minimize2 className="w-5 h-5 text-slate-400" />
                ) : (
                  <Maximize2 className="w-5 h-5 text-slate-400" />
                )}
              </button>
              <button
                onClick={onClose}
                className="p-2 rounded-xl transition-all hover:bg-white/10"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex flex-col lg:flex-row h-[calc(100%-60px)]">
            {/* Canvas */}
            <div 
              ref={containerRef}
              className="flex-1 relative flex items-center justify-center p-4"
            >
              <canvas
                ref={canvasRef}
                width={600}
                height={600}
                className="max-w-full max-h-full"
                style={{ 
                  borderRadius: '50%',
                  boxShadow: `0 0 60px ${theme.colors.primary}20`
                }}
              />

              {/* Rotation Controls */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
                <button
                  onClick={() => setViewRotation(r => r - 0.2)}
                  className="p-2 rounded-xl bg-black/50 hover:bg-black/70 transition-all"
                >
                  <ChevronLeft className="w-5 h-5" style={{ color: theme.colors.primary }} />
                </button>
                <button
                  onClick={() => setZoom(z => Math.max(0.5, z - 0.2))}
                  className="p-2 rounded-xl bg-black/50 hover:bg-black/70 transition-all"
                >
                  <ZoomOut className="w-5 h-5" style={{ color: theme.colors.primary }} />
                </button>
                <button
                  onClick={() => setZoom(z => Math.min(2, z + 0.2))}
                  className="p-2 rounded-xl bg-black/50 hover:bg-black/70 transition-all"
                >
                  <ZoomIn className="w-5 h-5" style={{ color: theme.colors.primary }} />
                </button>
                <button
                  onClick={() => setViewRotation(r => r + 0.2)}
                  className="p-2 rounded-xl bg-black/50 hover:bg-black/70 transition-all"
                >
                  <ChevronRight className="w-5 h-5" style={{ color: theme.colors.primary }} />
                </button>
              </div>

              {/* Loading Overlay */}
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                  <RefreshCw className="w-8 h-8 animate-spin" style={{ color: theme.colors.primary }} />
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div 
              className="w-full lg:w-80 p-4 border-t lg:border-t-0 lg:border-l overflow-y-auto"
              style={{ borderColor: `${theme.colors.primary}20` }}
            >
              {/* Toggle Options */}
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setShowConstellationLines(!showConstellationLines)}
                  className={`flex-1 px-3 py-2 rounded-xl text-xs font-bold transition-all ${
                    showConstellationLines ? 'bg-primary/20' : 'bg-white/5'
                  }`}
                  style={{ 
                    color: showConstellationLines ? theme.colors.primary : '#888',
                    border: `1px solid ${showConstellationLines ? theme.colors.primary + '50' : 'transparent'}`
                  }}
                >
                  Lines
                </button>
                <button
                  onClick={() => setShowLabels(!showLabels)}
                  className={`flex-1 px-3 py-2 rounded-xl text-xs font-bold transition-all ${
                    showLabels ? 'bg-primary/20' : 'bg-white/5'
                  }`}
                  style={{ 
                    color: showLabels ? theme.colors.primary : '#888',
                    border: `1px solid ${showLabels ? theme.colors.primary + '50' : 'transparent'}`
                  }}
                >
                  Labels
                </button>
              </div>

              {/* Moon Phase */}
              {celestialData?.moon_phase && (
                <div 
                  className="p-4 rounded-xl mb-4"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Moon className="w-5 h-5 text-[#ffd700]" />
                    <span className="font-bold text-white">Moon Phase</span>
                  </div>
                  <p className="text-lg font-unbounded" style={{ color: theme.colors.primary }}>
                    {celestialData.moon_phase}
                  </p>
                </div>
              )}

              {/* Celestial Objects */}
              <div className="mb-4">
                <h3 className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">
                  Celestial Bodies
                </h3>
                <div className="space-y-2">
                  {celestialData?.celestial_objects?.map((obj) => (
                    <motion.div
                      key={obj.name}
                      whileHover={{ scale: 1.02 }}
                      className="p-3 rounded-xl cursor-pointer transition-all"
                      style={{ 
                        background: selectedObject?.name === obj.name 
                          ? `${theme.colors.primary}20` 
                          : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${selectedObject?.name === obj.name ? theme.colors.primary + '50' : 'transparent'}`
                      }}
                      onClick={() => setSelectedObject(selectedObject?.name === obj.name ? null : obj)}
                    >
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-8 h-8 rounded-full flex items-center justify-center"
                          style={{ 
                            background: PLANETS[obj.name]?.color || '#ffd700',
                            boxShadow: `0 0 10px ${PLANETS[obj.name]?.color || '#ffd700'}50`
                          }}
                        >
                          {obj.type === 'satellite' && obj.name === 'ISS' ? (
                            <Satellite className="w-4 h-4 text-white" />
                          ) : obj.name === 'Moon' ? (
                            <Moon className="w-4 h-4 text-gray-800" />
                          ) : (
                            <span className="text-xs font-bold text-gray-800">{obj.name[0]}</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="font-bold text-white text-sm">{obj.name}</div>
                          <div className="text-xs text-slate-500">{obj.type}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs" style={{ color: theme.colors.primary }}>
                            {obj.brightness}
                          </div>
                        </div>
                      </div>
                      {selectedObject?.name === obj.name && (
                        <motion.p
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          className="mt-2 text-xs text-slate-400"
                        >
                          {obj.description}
                        </motion.p>
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Visible Constellations */}
              <div>
                <h3 className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">
                  Visible Constellations
                </h3>
                <div className="space-y-2">
                  {celestialData?.visible_constellations?.slice(0, 8).map((name) => (
                    <motion.div
                      key={name}
                      whileHover={{ scale: 1.02 }}
                      className="p-3 rounded-xl cursor-pointer transition-all"
                      style={{ 
                        background: selectedConstellation === name 
                          ? `${theme.colors.primary}20` 
                          : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${selectedConstellation === name ? theme.colors.primary + '50' : 'transparent'}`
                      }}
                      onClick={() => setSelectedConstellation(selectedConstellation === name ? null : name)}
                    >
                      <div className="flex items-center gap-3">
                        <Star className="w-4 h-4" style={{ color: theme.colors.primary }} />
                        <span className="font-medium text-white text-sm">{name}</span>
                      </div>
                      {selectedConstellation === name && CONSTELLATIONS[name] && (
                        <motion.p
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          className="mt-2 text-xs text-slate-400"
                        >
                          {CONSTELLATIONS[name].description}
                        </motion.p>
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default StarMap;
